// Темп бронирования — графики (Дашборд → Обзор). Данные — GET /reports/booking-series.
// Один отель на график (тёплая палитра бренда не даёт трёх различимых цветов,
// поэтому «По отдельности» — малые графики с общей шкалой, а не три линии в одном).
// В графике две линии: выбранный период (jasper, сплошная) и период сравнения
// (choco-500, пунктир) — пара проверена валидатором dataviz (CVD ΔE 16.8).
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import * as api from '../../utils/api';
import { fmt } from './helpers';

const METRICS = [
  { key: 'new_rev',  label: 'Брони, ₽',  money: true },
  { key: 'new_cnt',  label: 'Брони, шт' },
  { key: 'canc_rev', label: 'Отмены, ₽', money: true, bad: true },
  { key: 'canc_cnt', label: 'Отмены, шт', bad: true },
  { key: 'net_rev',  label: 'Нетто, ₽',  money: true },
  // Чистые — созданные в периоде и не отменённые на сейчас (без уже отменённых).
  { key: 'clean_rev', label: 'Чистые, ₽', money: true },
  { key: 'clean_cnt', label: 'Чистые, шт' },
];
const GRANS = [{ key: 'day', label: 'Дни' }, { key: 'week', label: 'Недели' }, { key: 'month', label: 'Месяцы' }];
const COMPARES = [{ key: 'none', label: 'Без сравнения' }, { key: 'prev', label: 'Прошлый период' }, { key: 'year', label: 'Год назад' }];
const MON = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

// Даты — строки YYYY-MM-DD по Москве.
const todayMsk = () => new Date(Date.now() + 3 * 3600000).toISOString().slice(0, 10);
const addDays = (s, n) => { const d = new Date(s + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const addMonths = (s, n) => { const d = new Date(s + 'T00:00:00Z'); d.setUTCDate(1); d.setUTCMonth(d.getUTCMonth() + n); return d.toISOString().slice(0, 10); };
const dm = s => `${+s.slice(8, 10)} ${MON[+s.slice(5, 7) - 1]}`;
const my = s => `${MON[+s.slice(5, 7) - 1]} ${s.slice(2, 4)}`;
const bucketLabel = (b, gran) => gran === 'month' ? my(b.from) : gran === 'week' ? `${dm(b.from)} – ${dm(b.to)}` : dm(b.from);
const tickLabel = (b, gran) => gran === 'month' ? my(b.from) : dm(b.from);

// Быстрые периоды по шагу.
const PRESETS = {
  day:   [{ key: 'd14', label: '14 дней', from: t => addDays(t, -13) }, { key: 'd30', label: '30 дней', from: t => addDays(t, -29) }, { key: 'd90', label: '90 дней', from: t => addDays(t, -89) }],
  week:  [{ key: 'w12', label: '12 недель', from: t => addDays(t, -7 * 11) }, { key: 'w26', label: '26 недель', from: t => addDays(t, -7 * 25) }, { key: 'ytd', label: 'С начала года', from: t => `${t.slice(0, 4)}-01-01` }],
  month: [{ key: 'm6', label: '6 месяцев', from: t => addMonths(t, -5) }, { key: 'm12', label: '12 месяцев', from: t => addMonths(t, -11) }, { key: 'ytd', label: 'С начала года', from: t => `${t.slice(0, 4)}-01-01` }],
};
const DEFAULT_PRESET = { day: 'd14', week: 'w12', month: 'ytd' };

const fmtAxis = (n, money) => {
  const a = Math.abs(n);
  if (!money) return String(Math.round(n));
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace('.', ',')} млн`;
  if (a >= 1e3) return `${Math.round(n / 1e3)} тыс`;
  return String(Math.round(n));
};
const fmtVal = (n, money) => money ? fmt(n) : `${n} шт`;
// «Красивый» шаг оси: 1/2/5 × 10^k.
function niceTicks(min, max, count = 4) {
  if (max === min) max = min + 1;
  const raw = (max - min) / count, p = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 5, 10].map(m => m * p).find(s => s >= raw);
  const lo = Math.floor(min / step) * step, hi = Math.ceil(max / step) * step;
  const out = []; for (let v = lo; v <= hi + step / 2; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

function Seg({ value, onChange, options }) {
  return (
    <div className="dbx-seg">
      {options.map(o => (
        <button key={o.key} type="button" className={value === o.key ? 'is-active' : ''} onClick={() => onChange(o.key)}>{o.label}</button>
      ))}
    </div>
  );
}

export default function BookingTrendChart() {
  const [hotel, setHotel] = useState('split');            // 'total' | property_id | 'split'; по умолчанию — отели по отдельности
  const [metric, setMetric] = useState('new_rev');
  const [gran, setGran] = useState('day');            // по умолчанию — по дням за 14 дней
  const [preset, setPreset] = useState(DEFAULT_PRESET.day);
  const [range, setRange] = useState(() => ({ from: PRESETS.day[0].from(todayMsk()), to: todayMsk() }));
  const [compare, setCompare] = useState('prev');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [sharedScale, setSharedScale] = useState(true);   // «По отдельности»: общая шкала (сравнить отели) или своя (видна динамика малых)

  const applyPreset = (g, key) => {
    const p = PRESETS[g].find(x => x.key === key) || PRESETS[g][0];
    const t = todayMsk();
    setPreset(p.key); setRange({ from: p.from(t), to: t });
  };
  const changeGran = g => { setGran(g); applyPreset(g, DEFAULT_PRESET[g]); };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/reports/booking-series?gran=${gran}&from=${range.from}&to=${range.to}&compare=${compare}`)
      .then(d => { if (alive) { setData(d); setErr(''); setLoading(false); } })
      .catch(e => { if (alive) { setErr(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [gran, range, compare]);

  const m = METRICS.find(x => x.key === metric);
  const hotelOpts = [{ key: 'total', label: 'Все отели' }, ...((data?.hotels) || []).map(h => ({ key: String(h.property_id), label: h.short_name })), { key: 'split', label: 'По отдельности' }];
  const entities = !data ? [] : hotel === 'split' ? data.hotels : hotel === 'total' ? [data.total] : data.hotels.filter(h => String(h.property_id) === hotel);
  // Общая шкала для малых графиков — сравнимы между отелями.
  const allVals = entities.flatMap(e => [...e.points, ...(e.compare || [])]).filter(Boolean).map(p => p[metric]);
  const yDomain = [Math.min(0, ...allVals), Math.max(1, ...allVals)];
  const ownDomain = e => { const v = [...e.points, ...(e.compare || [])].filter(Boolean).map(p => p[metric]); return [Math.min(0, ...v), Math.max(1, ...v)]; };
  const cmpEmpty = data?.compare_buckets && entities.every(e => (e.compare || []).every(p => p == null));

  return (
    <section className="dbx-section">
      <div className="dbx-section__head">
        <div>
          <span className="dbx-kicker">Темп бронирования</span>
          <h2 className="dbx-section__title">Динамика продаж</h2>
          <div className="dbx-section__sub">
            Новые брони — по дате создания, отмены — по дате отмены, по Москве. Сумма — вся бронь.
            {' '}<b>Нетто</b> — новые минус все отмены за период; <b>чистые</b> — брони, созданные в периоде, без тех, что уже отменены.
          </div>
        </div>
      </div>

      {/* Фильтры — одной полосой над графиком */}
      <div className="dbx-filters">
        <Seg value={hotel} onChange={setHotel} options={hotelOpts} />
        <Seg value={metric} onChange={setMetric} options={METRICS} />
      </div>
      <div className="dbx-filters">
        <Seg value={gran} onChange={changeGran} options={GRANS} />
        <Seg value={preset} onChange={k => applyPreset(gran, k)} options={PRESETS[gran]} />
        <div className="dbx-range">
          <input type="date" value={range.from} max={range.to} onChange={e => { setPreset(''); setRange(r => ({ ...r, from: e.target.value })); }} aria-label="С" />
          <span>—</span>
          <input type="date" value={range.to} min={range.from} max={todayMsk()} onChange={e => { setPreset(''); setRange(r => ({ ...r, to: e.target.value })); }} aria-label="По" />
        </div>
        <Seg value={compare} onChange={setCompare} options={COMPARES} />
        {hotel === 'split' && (
          <Seg value={sharedScale ? 'shared' : 'own'} onChange={k => setSharedScale(k === 'shared')}
            options={[{ key: 'shared', label: 'Общая шкала' }, { key: 'own', label: 'Своя шкала' }]} />
        )}
      </div>

      {err && <p className="dbx-note" style={{ color: 'var(--jasper-600)' }}>{err}</p>}
      {!data && loading && <p className="dbx-note">Загрузка…</p>}
      {data && (
        <div style={{ opacity: loading ? 0.5 : 1, transition: 'opacity var(--transition-fast)', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {compare !== 'none' && cmpEmpty && (
            <div className="dbx-callout">
              {compare === 'year'
                ? 'Сравнения с прошлым годом пока нет: даты создания броней в TravelLine есть с февраля 2026 (в январе архив перенесён одной датой). Линия «год назад» начнёт заполняться с февраля 2027. Сейчас для сравнения — «Прошлый период».'
                : 'Период сравнения приходится на время до февраля 2026 — дат создания броней тогда в TravelLine нет (в январе архив перенесён одной датой). Возьмите период покороче.'}
            </div>
          )}
          <div className={hotel === 'split' ? 'dbx-charts-grid' : ''}>
            {entities.map(e => (
              <div key={e.property_id ?? 'total'} className="dbx-card dbx-chart-card">
                <ChartCard entity={e} data={data} metric={m} yDomain={hotel === 'split' && !sharedScale ? ownDomain(e) : yDomain} small={hotel === 'split'} />
              </div>
            ))}
          </div>
          {data.truncated && <p className="dbx-note">Период обрезан — слишком много точек для выбранного шага; возьмите шаг крупнее.</p>}
          <button type="button" className="dbx-link" style={{ alignSelf: 'flex-start' }} onClick={() => setShowTable(v => !v)}>
            {showTable ? 'Скрыть таблицу' : 'Показать таблицей'}
          </button>
          {showTable && <DataTable data={data} entities={entities} metric={m} />}
        </div>
      )}
    </section>
  );
}

// ─── Карточка с графиком одного отеля ─────────────────────────────────────────
function ChartCard({ entity, data, metric, yDomain, small }) {
  const pts = entity.points.map(p => (p ? p[metric.key] : null));
  const cmp = entity.compare ? entity.compare.map(p => (p ? p[metric.key] : null)) : null;
  const sum = a => a.reduce((s, v) => s + (v || 0), 0);
  const total = sum(pts), cmpTotal = cmp ? sum(cmp) : null;
  const hasCmp = cmp && cmp.some(v => v != null);
  return (
    <>
      <div className="dbx-chart-head">
        <span className="dbx-hotel">{entity.short_name}</span>
        <div className="dbx-chart-total">
          <span className="dbx-lbl">Итого за период</span>
          <span className="dbx-mid">{fmtVal(Math.round(total * 100) / 100, metric.money)}</span>
          {hasCmp && <span className="dbx-cmp"><Delta cur={total} prev={cmpTotal} goodUp={!metric.bad} /> <span>сравнение {fmtVal(Math.round(cmpTotal * 100) / 100, metric.money)}</span></span>}
        </div>
      </div>
      <LineChart buckets={data.buckets} cmpBuckets={data.compare_buckets} gran={data.gran} pts={pts} cmp={hasCmp ? cmp : null}
        metric={metric} yDomain={yDomain} height={small ? 220 : 320} />
      <div className="dbx-legend">
        <span><i className="dbx-key" /> Выбранный период</span>
        {hasCmp && <span><i className="dbx-key is-cmp" /> {data.compare === 'year' ? 'Год назад' : 'Прошлый период'}</span>}
        {data.buckets.some(b => b.partial) && <span><i className="dbx-key is-dot" /> Текущий период ещё идёт</span>}
      </div>
    </>
  );
}

function Delta({ cur, prev, goodUp = true }) {
  if (prev == null || (!prev && !cur)) return <span className="dbx-delta is-flat">—</span>;
  if (!prev) return <span className={'dbx-delta ' + (goodUp ? 'is-good' : 'is-bad')}>новое</span>;
  if (prev < 0) { const d = cur - prev; return <span className={'dbx-delta ' + ((d > 0) === goodUp ? 'is-good' : 'is-bad')}>{d >= 0 ? '+' : '−'}{fmt(Math.abs(d))}</span>; }
  const d = (cur - prev) / Math.abs(prev);
  const cls = Math.abs(d) < 0.005 ? 'is-flat' : ((d >= 0) === goodUp ? 'is-good' : 'is-bad');
  return <span className={'dbx-delta ' + cls}>{d >= 0 ? '+' : '−'}{Math.round(Math.abs(d) * 100)}%</span>;
}

// Ширина контейнера (SVG рисуем в реальных пикселях — текст не искажается).
function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(600);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(260, Math.floor(e.contentRect.width))));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// ─── Линейный график: текущий период + сравнение, crosshair и подсказка ──────
function LineChart({ buckets, cmpBuckets, gran, pts, cmp, metric, yDomain, height }) {
  const [ref, W] = useWidth();
  const [hover, setHover] = useState(null);
  const M = { t: 16, r: 20, b: 30, l: 64 };
  const iw = W - M.l - M.r, ih = height - M.t - M.b;
  const n = buckets.length;
  const x = i => M.l + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
  const ticks = niceTicks(yDomain[0], yDomain[1]);
  const y0 = ticks[0], y1 = ticks[ticks.length - 1];
  const y = v => M.t + ih - ((v - y0) / (y1 - y0)) * ih;
  // Путь по точкам, разрывы на null (нет данных).
  const path = arr => arr.reduce((d, v, i) => v == null ? d : d + `${d && arr[i - 1] != null ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`, '');
  const area = arr => {
    const segs = []; let cur = [];
    arr.forEach((v, i) => { if (v == null) { if (cur.length) segs.push(cur); cur = []; } else cur.push(i); });
    if (cur.length) segs.push(cur);
    return segs.filter(s => s.length > 1).map(s => `M${x(s[0])},${y(Math.max(0, y0))}` + s.map(i => `L${x(i).toFixed(1)},${y(arr[i]).toFixed(1)}`).join('') + `L${x(s[s.length - 1])},${y(Math.max(0, y0))}Z`).join('');
  };
  // Подписи X — не больше ~8, чтобы не слипались.
  const every = Math.max(1, Math.ceil(n / Math.max(2, Math.floor(iw / 90))));
  const lastIdx = pts.map((v, i) => (v == null ? -1 : i)).filter(i => i >= 0).pop();
  const partialIdx = buckets.findIndex(b => b.partial);

  const onMove = e => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left;
    const i = n === 1 ? 0 : Math.round(((px - M.l) / iw) * (n - 1));
    setHover(Math.min(n - 1, Math.max(0, i)));
  };
  const onKey = e => {
    if (e.key === 'ArrowRight') setHover(h => Math.min(n - 1, (h ?? -1) + 1));
    if (e.key === 'ArrowLeft') setHover(h => Math.max(0, (h ?? n) - 1));
  };

  return (
    <div ref={ref} className="dbx-chart" style={{ height }}>
      <svg width={W} height={height} role="img" aria-label={`${metric.label}: график по периодам`}
        tabIndex={0} onMouseMove={onMove} onMouseLeave={() => setHover(null)} onKeyDown={onKey} onBlur={() => setHover(null)}>
        {/* сетка и ось Y */}
        {ticks.map(t => (
          <g key={t}>
            <line x1={M.l} x2={W - M.r} y1={y(t)} y2={y(t)} stroke={t === 0 ? 'var(--border-strong)' : 'var(--border-hairline)'} strokeWidth="1" />
            <text x={M.l - 10} y={y(t)} dy="0.32em" textAnchor="end" className="dbx-axis">{fmtAxis(t, metric.money)}</text>
          </g>
        ))}
        {/* ось X */}
        {buckets.map((b, i) => (i % every === 0 || i === n - 1) && (i === n - 1 || n - 1 - i >= every / 2) ? (
          <text key={i} x={x(i)} y={height - 8} textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'} className="dbx-axis">{tickLabel(b, gran)}</text>
        ) : null)}
        {/* сравнение — пунктир, приглушённый */}
        {cmp && <path d={path(cmp)} fill="none" stroke="var(--choco-500)" strokeWidth="2" strokeDasharray="5 4" strokeLinejoin="round" strokeLinecap="round" />}
        {/* выбранный период — заливка-вуаль + линия */}
        <path d={area(pts)} fill="var(--jasper-500)" opacity="0.1" />
        <path d={path(pts)} fill="none" stroke="var(--jasper-500)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {/* текущий (неполный) период — полая точка */}
        {partialIdx >= 0 && pts[partialIdx] != null && (
          <circle cx={x(partialIdx)} cy={y(pts[partialIdx])} r="5" fill="var(--surface-card)" stroke="var(--jasper-500)" strokeWidth="2" />
        )}
        {/* значение на конце линии */}
        {lastIdx != null && lastIdx !== partialIdx && (
          <circle cx={x(lastIdx)} cy={y(pts[lastIdx])} r="4" fill="var(--jasper-500)" stroke="var(--surface-card)" strokeWidth="2" />
        )}
        {/* crosshair */}
        {hover != null && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={M.t} y2={M.t + ih} stroke="var(--choco-300)" strokeWidth="1" />
            {cmp && cmp[hover] != null && <circle cx={x(hover)} cy={y(cmp[hover])} r="4" fill="var(--choco-500)" stroke="var(--surface-card)" strokeWidth="2" />}
            {pts[hover] != null && <circle cx={x(hover)} cy={y(pts[hover])} r="5" fill="var(--jasper-500)" stroke="var(--surface-card)" strokeWidth="2" />}
          </g>
        )}
      </svg>
      {hover != null && (
        <div className="dbx-tip" style={{ left: Math.min(Math.max(x(hover) - 110, 0), W - 230), top: 8 }}>
          <div className="dbx-tip__row">
            <i className="dbx-key" />
            <div>
              <b>{pts[hover] == null ? 'нет данных' : fmtVal(pts[hover], metric.money)}</b>
              <span>{bucketLabel(buckets[hover], gran)}{buckets[hover].partial ? ' · ещё идёт' : ''}</span>
            </div>
          </div>
          {cmp && cmpBuckets && (
            <div className="dbx-tip__row">
              <i className="dbx-key is-cmp" />
              <div>
                <b>{cmp[hover] == null ? 'нет данных' : fmtVal(cmp[hover], metric.money)}</b>
                <span>{bucketLabel(cmpBuckets[hover], gran)}{buckets[hover].partial ? ' · на тот же момент' : ''}</span>
              </div>
            </div>
          )}
          {cmp && pts[hover] != null && cmp[hover] != null && (
            <div className="dbx-tip__delta"><Delta cur={pts[hover]} prev={cmp[hover]} goodUp={!metric.bad} /></div>
          )}
        </div>
      )}
    </div>
  );
}

// Табличный вид — те же значения без наведения.
function DataTable({ data, entities, metric }) {
  const v = p => (p == null ? '—' : metric.money ? fmt(p[metric.key]) : p[metric.key]);
  return (
    <div className="dbx-card dbx-table-wrap">
      <table className="dbx-table">
        <thead>
          <tr>
            <th>Период</th>
            {entities.map(e => <th key={e.property_id ?? 't'}>{e.short_name}</th>)}
            {data.compare_buckets && entities.map(e => <th key={'c' + (e.property_id ?? 't')} className={e === entities[0] ? 'dbx-sep' : ''}>{e.short_name} · сравн.</th>)}
          </tr>
        </thead>
        <tbody>
          {data.buckets.map((b, i) => (
            <tr key={b.from}>
              <td>{bucketLabel(b, data.gran)}{b.partial ? ' *' : ''}</td>
              {entities.map(e => <td key={e.property_id ?? 't'}>{v(e.points[i])}</td>)}
              {data.compare_buckets && entities.map(e => <td key={'c' + (e.property_id ?? 't')} className={e === entities[0] ? 'dbx-sep' : ''}>{v(e.compare[i])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
